import { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ShoppingCart } from 'lucide-react'
import { useQuoteBasketStore } from '@/stores/quoteBasketStore'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

interface VisibilitySettings {
  show_about: boolean
  show_products: boolean
  show_gallery: boolean
  show_certificates: boolean
  show_contact: boolean
}

const defaultVisibility: VisibilitySettings = {
  show_about: true,
  show_products: true,
  show_gallery: true,
  show_certificates: true,
  show_contact: true,
}

const allNavLinks = [
  { to: '/', label: 'Home', key: null }, // Always visible
  { to: '/about', label: 'About', key: 'show_about' },
  { to: '/products', label: 'Products & Services', key: 'show_products' },
  { to: '/gallery', label: 'Gallery', key: 'show_gallery' },
  { to: '/certificates', label: 'Certificates', key: 'show_certificates' },
  { to: '/contact', label: 'Contact', key: 'show_contact' },
]

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const itemCount = useQuoteBasketStore((state) => state.getItemCount())

  const { data: visibilitySettings } = useQuery({
    queryKey: ['visibility-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'visibility_settings')
        .single()
      if (error) return defaultVisibility
      return (data?.value as VisibilitySettings) || defaultVisibility
    },
  })

  const visibility = visibilitySettings || defaultVisibility

  // Filter nav links based on visibility settings
  const navLinks = allNavLinks.filter((link) => {
    if (link.key === null) return true // Home always visible
    return visibility[link.key as keyof VisibilitySettings]
  })

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-secondary-100">
      <div className="container-custom">
        <div className="flex items-center justify-between h-24 lg:h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center -ml-4 sm:-ml-8 lg:-ml-16 xl:-ml-20">
            <img
              src="/images/logo.png"
              alt="EngiMarineX"
              className="h-20 lg:h-24 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    isActive
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Quote Basket */}
            <Link
              to="/quote-basket"
              className="relative p-2 text-secondary-600 hover:text-primary-600 transition-colors"
            >
              <ShoppingCart className="w-6 h-6" />
              {itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-primary-600 text-white text-xs font-bold rounded-full"
                >
                  {itemCount}
                </motion.span>
              )}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-secondary-600 hover:text-secondary-900"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-secondary-100 bg-white"
          >
            <nav className="container-custom py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'px-4 py-3 text-base font-medium rounded-lg transition-colors',
                      isActive
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
